import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { pluralizeToString } from 'lib/pluralize'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import { CABIN_CATEGORIES } from 'constants/cruise'
import AutoTooltip, { AutoTooltipContent } from 'components/Common/AutoTooltip'
import { offerHasValidDepartures } from 'lib/Cruise/cruiseUtils'
import moment from 'moment'
import cn from 'clsx'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import InlineGroup from 'components/Common/InlineGroup'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import { getCruiseOfferPriceVariation } from 'lib/Cruise/calculatingPrices'
import Pill from 'components/Luxkit/Pill'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import SolidCheckIcon from 'components/Luxkit/Icons/solid/SolidCheckIcon'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import useQueryParams from 'hooks/useQueryParams'
import { sortBy, without } from 'lib/arrayUtils'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import List from 'components/Common/List/List'
import Typography from 'components/Luxkit/Typography/Typography'
import Label from 'components/Luxkit/Label/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import { shallowEqual } from 'react-redux'

const DetailsCaption = styled(BodyText)`
  align-items: center;
  display: flex;

  > * + *::before {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: "·";
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const DetailsCaptionInfo = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
`

const OperatorLogoImage = styled(ResponsiveImage)`
  align-self: flex-end;

  ${mediaQueryUp.desktop} {
    width: ${rem(150)};
    margin: 0 auto;
  }

`

const Cruise = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.five};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    padding: ${rem(20)} ${rem(20)} 0;
    display: grid;
    grid-template-areas:
      "img description description description"
      "separator separator separator separator"
      "prices prices prices prices"
      "see-prices see-prices see-prices see-prices";
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    grid-template-rows: 3fr 0.5fr 1fr;
  }
`

const FooterCaption = styled.div`
  font-size: smaller;
  color: ${props => props.theme.palette.neutral.three};
`

const TileMediaContainer = styled.div`
  display: flex;
  position: relative;
  height: ${rem(230)};

  ${mediaQueryUp.desktop} {
    height: 100%;
  }
`

const Description = styled.div`
  padding: ${rem(20)};
  position: relative;

  ${mediaQueryUp.desktop} {
    padding: 0 0 0 ${rem(20)};
    grid-area: description;
  }
`

const DesktopBookmarkContainer = styled(FloatingBookmarkContainer)`
  top: unset;
`

const CruiseTitle = styled.div`
  margin: ${rem(4)} 0 ${rem(12)};
`

const Details = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${rem(12)};
  height: 100%;
`

const CruiseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const ItineraryTooltip = styled(AutoTooltip)`
  display: none;
  > span {
    text-decoration: underline;
  }

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

const DepartureDateTooltip = styled(AutoTooltip)`
  display: none;

  > span {
    text-decoration: underline;
    margin-left: ${rem(4)};

    &:hover + div {
      transition: opacity 0.3s ease !important;
    }
  }

  ${mediaQueryUp.desktop} {
    display: inline-block;
  }
`

const DepartureDateWrapper = styled.div`
  margin-top: ${rem(8)};
  display: flex;
  align-items: center;
`

const Separator = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.palette.neutral.five};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  margin: 0 0 ${rem(20)} 5%;
  max-width: 90%;

  ${mediaQueryUp.desktop} {
    max-width: 100%;
    max-height: ${rem(1)};
    grid-area: separator;
    margin: ${rem(20)} 0 ;
  }
`

const Prices = styled.div`
  padding: 0 ${rem(20)} ${rem(20)};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(4)};
  .priceSize{
    font-size: 10px;
  }

  ${mediaQueryUp.desktop} {
    padding: 0 0 ${rem(16)};
    grid-area: prices;
    display: flex;
  }
`

const CabinPrice = styled.div`
  width: 100%;

  &.defocus {
    opacity: 0.5;
  }
`

const SeeAnotherPrices = styled(Group)`
  grid-area: see-prices;
  padding: 0 ${rem(16)} ${rem(16)};
`

const ButtonContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)} ${rem(20)};
    grid-template-columns: 2fr 1fr;
    align-items: center;
    display: grid;
    grid-area: cta;
    display: flex;
    justify-content: center;
    flex-direction: column-reverse;
    padding: 0 0 0 ${rem(20)};
  }
`

const ButtonContainerMobile = styled(ButtonContainer)`
  display: flex;
  justify-content: flex-end;
  grid-column: 2;
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const OfferButton = styled(TextButton)`
  width: 100%;

  ${mediaQueryUp.desktop} {
    min-width: ${rem(170)};
    min-height: ${rem(40)};
  }
`

const PromotionDescription = styled.div`
  margin-top: ${rem(12)};

  > * + * {
    margin-top: ${rem(4)};
  }
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  padding: ${rem(16)} 0;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    justify-content: space-between;
    padding: 0;
  }
`

const BadgeContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  mobileAspectRatio: '16:9',
  tabletAspectRatio: '16:9',
  desktopAspectRatio: '16:9',
  largeDesktopAspectRatio: '16:9',
  mobileWidth: '100vw',
  tabletWidth: '100vw',
  desktopWidth: '750px',
  largeDesktopWidth: '750px',
}

interface Props {
  cruise: App.CruiseOffer;
}

function CruiseSearchTile(props: Props) {
  const { cruise } = props
  const { departurePort, returnPort, itinerary, hasPromotions } = cruise
  const { lowestOverallPriceDetails, lowestPriceDetailsByCategory } = cruise.mainDepartureDetails
  const { promotion, discountPills } = lowestOverallPriceDetails
  const promotionDetails = promotion ?? cruise.fallbackPromotion
  const queryParams = useQueryParams()

  const { isVelocityEnabled } = useAppSelector((state) => ({
    isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
  }), shallowEqual)

  const departureMonths = queryParams.get('departureMonths')
  const cities = itinerary?.map((event) => {
    const { port } = event
    if (!port) return
    return port.city || port.name.split(',')[0]
  }).filter((city) => city && city.length > 1)

  const countries = itinerary?.map((itineraryItem) => {
    const { port } = itineraryItem
    if (!port) return
    return port.countryCode
  }).filter(country => country && country.length)

  const placesCount = [...new Set(cities)].length
  const countriesCount = [...new Set(countries)].length

  const hasTheSameStartAndEndPort = departurePort === returnPort
  const departureCity = useMemo(() => departurePort?.split(',')?.[0], [departurePort])
  const returnCity = useMemo(() => returnPort?.split(',')?.[0], [returnPort])

  const departureDates = useMemo(() => {
    const momentDates = cruise.departureDates?.map(date => moment(date)) ?? []
    const sortedDates = sortBy(momentDates, date => date.valueOf(), 'asc')
    if (departureMonths) {
      const earliestMatchingDate = sortedDates.find(date => departureMonths.includes(date.format('YYYY-MM'))) ?? sortedDates[0]

      return [
        earliestMatchingDate,
        ...without(sortedDates, earliestMatchingDate),
      ].map(date => date.format(SHORT_DAY_NAME_DAY_MONTH_YEAR))
    }
    return sortedDates.map(date => date.format(SHORT_DAY_NAME_DAY_MONTH_YEAR))
  }, [cruise.departureDates, departureMonths])

  const [earliestDepartureDate, ...otherDepartureDates] = departureDates

  const hasDepartures = useMemo(() => offerHasValidDepartures(cruise), [cruise])

  if (!hasDepartures) {
    // departures are mandatory and the api can sometimes return offers that don't have time
    // so we need to manually protect against them
    // TODO: REmove when API is fixed
    throw new Error('no departures')
  }

  const pricePerCategory = useMemo(() => {
    return Object.entries(lowestPriceDetailsByCategory).map(([category, price]) => {
      const categoryIncluded = CABIN_CATEGORIES.some((item) => item.category === category)
      if (categoryIncluded) return price
    }).filter(Boolean).sort((a, b) => (a?.total ?? 0) - (b?.total ?? 0))
  }, [lowestPriceDetailsByCategory])

  const cabinTypes = queryParams.getAll('cabinTypes')
  const minPrice = parseFloat(queryParams.get('price.min') || '0')
  const maxPrice = parseFloat(queryParams.get('price.max') || '0')

  const hasHighlightedPrices = useMemo(() => {
    return (pricePerCategory || []).some((price) => {
      const total = price?.total ?? 0
      if (!total) return false
      return (!minPrice || total >= minPrice) && (!maxPrice || total <= maxPrice)
    })
  }, [pricePerCategory, minPrice, maxPrice])

  const offerDetails = useMemo(() => {
    return <div>
      <Caption variant="large">
        Operated by: <strong>{cruise.cruiseLine.name}</strong>
      </Caption>
      <Caption variant="large">
        Cruise ship: <strong>{cruise.ship.name}</strong>
      </Caption>
      <DetailsCaption variant="small" weight="bold">
        <DetailsCaptionInfo>{pluralizeToString('night', cruise.duration)}</DetailsCaptionInfo>
        {!!placesCount && <DetailsCaptionInfo>{pluralizeToString('port', placesCount)}</DetailsCaptionInfo>}
        {!!countriesCount && <DetailsCaptionInfo>{pluralizeToString('country', countriesCount)}</DetailsCaptionInfo>}
        <ItineraryTooltip>
          View itinerary
          <AutoTooltipContent width={250} type="dark" position="below">
            <BodyText variant="small">
              {[...new Set(cities)].join(' - ')}
            </BodyText>
          </AutoTooltipContent>
        </ItineraryTooltip>
      </DetailsCaption>

      <VerticalSpacer gap={12}>
        {promotionDetails?.sellingPoints && (
          <PromotionDescription>
            <BodyText weight="semi-bold" variant="medium">
              Inclusions (select packages only)
            </BodyText>
            {promotionDetails.sellingPoints.map(
              (point, index) => {
                const isHighlighted = index === 0 && !promotionDetails?.onBoardCredit
                if (!isHighlighted) {
                  return (
                    <BodyTextBlock key={point} weight="normal" variant="small" startIcon={<SolidCheckIcon/>}>
                      {point}
                    </BodyTextBlock>
                  )
                } })
            }
          </PromotionDescription>
        )}
        {promotionDetails?.sellingPoints && !promotionDetails?.onBoardCredit && (
          <PromotionDescription>
            <BodyTextBlock colour="highlight" weight="bold" variant="medium" startIcon={<LinePricetagAltIcon size="S"/>}>
              {promotionDetails.sellingPoints[0]}
            </BodyTextBlock>
          </PromotionDescription>
        )}
        {promotionDetails?.onBoardCredit && (
          <PromotionDescription>
            <BodyTextBlock colour="highlight" weight="bold" variant="medium" startIcon={<LinePricetagAltIcon size="S"/>}>
              Up to <FormatCurrency currency={promotionDetails.onBoardCredit.currencyCode} value={promotionDetails.onBoardCredit.amount ?? 0} /> onboard credit available
            </BodyTextBlock>
          </PromotionDescription>
        )}

        <DepartureDateWrapper>
          <BodyText variant="medium" weight="bold" >
            Departing on {earliestDepartureDate}
            {otherDepartureDates?.length > 0 &&
              <DepartureDateTooltip>
                and more
                <AutoTooltipContent type="dark" position="above">
                  <VerticalSpacer gap={4}>
                    <BodyText variant="medium" wrap="no-wrap" weight="bold">Other departure dates</BodyText>
                    <Typography align="start">
                      <List>
                        {otherDepartureDates.map(date => <BodyText key={date} variant="small" as="li">{date}</BodyText>)}
                      </List>
                    </Typography>
                  </VerticalSpacer>
                </AutoTooltipContent>
              </DepartureDateTooltip>
            }
          </BodyText>
        </DepartureDateWrapper>
      </VerticalSpacer>
    </div>
  }, [cruise, placesCount, countriesCount, promotionDetails, earliestDepartureDate, otherDepartureDates, cities])

  return (
    <Cruise>
      <TileMediaContainer>
        <ImageCarousel
          topLeft={
            <BadgeContainer>
              <ProductTypeLabel productType={cruise.productType} />
              {isVelocityEnabled && <PartnershipLabel canEarn canUse />}
            </BadgeContainer>}
          images={cruise.images}
          imageParams={MEDIA_IMAGE_PARAMS}
          eagerLoadFirstImage
        />
      </TileMediaContainer>
      <Description>
        <Details>
          <CruiseInfo>
            <LabelGroup>
              {promotionDetails?.isNew && <Label kind="tertiary" variant="ghost">NEW</Label>}
              {promotionDetails && promotionDetails.isEndingIn <= 7 && <Label kind="primary" variant="urgency">OFFER ENDS IN {pluralizeToString('day', promotionDetails?.isEndingIn).toLocaleUpperCase()}</Label>}
              {hasPromotions && <Label startIcon={<SolidFireIcon />} kind="primary" variant="highlight-secondary">Special offer</Label>}
            </LabelGroup>
            <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon aria-hidden="true" />}>
              {hasTheSameStartAndEndPort && (
                <span>
                  Starts and ends in <strong>{returnCity}</strong>
                </span>
              )}
              {!hasTheSameStartAndEndPort && (
                <span>
                  Starts in <strong>{departureCity}</strong>. Ends in <strong>{returnCity}</strong>
                </span>
              )}
            </BodyTextBlock>
            <CruiseTitle>
              <Heading variant="heading4" format="titlecase" lineClamp={2}>
                {cruise.name}
              </Heading>
            </CruiseTitle>

            <CSSBreakpoint min="desktop">
              {offerDetails}
            </CSSBreakpoint>
          </CruiseInfo>

          <Container>
            <div>
              <TripGuard>
                <CSSBreakpoint max="tablet">
                  <OffsetBookmarkContainer>
                    <BookmarkButton offer={cruise} />
                  </OffsetBookmarkContainer>
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop">
                  <DesktopBookmarkContainer>
                    <BookmarkButton offer={cruise} />
                  </DesktopBookmarkContainer>
                </CSSBreakpoint>
              </TripGuard>
            </div>
            {cruise.cruiseLine.imageId && (
              <OperatorLogoImage
                id={cruise.cruiseLine.imageId}
                mobileWidth="75px"
                tabletWidth="150px"
                quality="eco"
                alt={cruise.cruiseLine.name}
                role="presentation"
                />
            )}

            <VerticalSpacer gap={16}>
              {!cruise.disableDeposits && <InlineGroup horizontal="end">
                {promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                  Secure with a {promotionDetails.deposit.formattedValue} deposit
                </Pill>}
                {!promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                  Deposits available
                </Pill>}
              </InlineGroup>}

              <ButtonContainer>
                <OfferButton kind="primary" nonInteractable>
                  View offer
                </OfferButton>
              </ButtonContainer>
            </VerticalSpacer>
          </Container>
        </Details>

        <CSSBreakpoint max="tablet">
          {offerDetails}
        </CSSBreakpoint>
      </Description>
      <Separator />
      <Prices>
        {pricePerCategory?.map((price) => {
          if (price) {
            const { primaryPrice, secondaryPrice } = getCruiseOfferPriceVariation(cruise, price)
            const isHighlightedPrice = (
              (!minPrice || price.total >= minPrice) &&
              (!maxPrice || price.total <= maxPrice) &&
              (!cabinTypes?.length || cabinTypes.includes(price.cabinCategory))
            )

            return (
              <CabinPrice key={price.cabinCategory} className={cn({ defocus: !isHighlightedPrice })}>
                <BodyText variant="small" colour="neutral-three">{price.cabinCategory} from</BodyText>
                <Heading variant="heading6" as="span">
                  <FormatCurrency value={primaryPrice?.total ?? 0} />
                </Heading>
                <BodyText variant="small" weight="semi-bold" as="span">
                  /{primaryPrice?.type}
                </BodyText>

                {secondaryPrice && <div>
                  <Caption variant="large" colour="neutral-three" as="spam">
                    <FormatCurrency value={secondaryPrice.total ?? 0} />
                  </Caption>
                  <Caption variant="large" colour="neutral-three" as="spam">
                    /{secondaryPrice.type}
                  </Caption>
                </div>}

                <BodyText variant="small" colour="neutral-three">
                  Twin Share
                </BodyText>
              </CabinPrice>
            )
          }
        })}

        {discountPills?.discountPercentage &&
          <CabinPrice>
            <ValueWrapper variant="large" weight="bold">
              <Pill kind="primary" variant="success">
                Save up to {discountPills.discountPercentage}%
              </Pill>
              <CruiseDiscountTermsAndConditions />
              <FooterCaption>
                All prices include taxes & fees
              </FooterCaption>
            </ValueWrapper>
          </CabinPrice>
        }
        <ButtonContainerMobile>
          <CabinPrice>
            <OfferButton kind="primary" nonInteractable>
              View offer
            </OfferButton>
          </CabinPrice>
        </ButtonContainerMobile>
      </Prices>

      {!hasHighlightedPrices && <SeeAnotherPrices direction="horizontal" horizontalAlign="center">
        <BodyTextBlock colour="highlight" weight="bold" variant="medium">
          See prices for other departures
        </BodyTextBlock>
      </SeeAnotherPrices>}
    </Cruise>
  )
}

export default React.memo(CruiseSearchTile)
